import "./style.scss";

import { Modal } from "@mui/material";
import DocoonSendOptionsFields, {
    type DocoonSendOptionsValue,
    getDefaultDocoonSendOptions,
    isDocoonSendOptionsComplete,
    toDocoonSendOptionsPayload,
} from "components/shared/DocoonSignModal/DocoonSendOptionsFields";
import DocoonSignatoryStep, {
    type CoBeneficiarySigner,
    toInitialSignatoryBlocks,
} from "components/shared/DocoonSignModal/DocoonSignatoryStep";
import MultiStep from "components/shared/MultiStep";
import StyledButton from "components/shared/StyledButton";
import type { AgreementRelatedResourceData } from "containers/ContractValidationNew";
import { SmallBody } from "helpers/TypographyComponents";
import { preventSubmit } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useSignatureProvider } from "hooks/useSignatureProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { DocoonSignatory, DocoonSignatoryWithRole, SignatureProvider } from "store/agreementsStore";
import {
    fetchAgreementsTemplate,
    fetchDocoonSignatories,
    postAgreement,
    toDocoonSignatoriesPayload,
} from "store/agreementsStore";
import { fetchChannels } from "store/channelStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTermAssociatedToTitle } from "store/titleStore";
import { fetchWorkTerms } from "store/workStore";

import AvenantTargetStep, { type AvenantTarget } from "./AvenantTargetStep";
import BeneficiariesStep from "./BeneficiariesStep";
import ChooseTemplateStep from "./ChooseTemplateStep";
import PickResourceStep from "./PickResourceStep";
import SignatoriesStep from "./SignatoriesStep";
import SignatureBehaviorStep, { type SignatureBehavior } from "./SignatureBehaviorStep";
import type { TFormCreateAgreement, TTitleBeneficiaries } from "./utils";
import { getBeneficiariesOptions } from "./utils";

interface AgreementValidationModalProps {
    /**
     * Related resource data for agreement validation modal and post agreement.
     * When omitted, the modal opens a resource picker as first step.
     */
    relatedResourceData?: AgreementRelatedResourceData;
    open: boolean;
    /**
     * Close modal action. Pass agreementId when contract was created to redirect to it.
     */
    onClose: (hasCreated?: boolean, agreementId?: number) => void;
    /**
     * Modal Type for single or multi signature
     */
    modalType?: "multiple" | "single";
    /**
     * Signature provider — defaults to Crealo Sign
     */
    signatureProvider?: SignatureProvider;
    /**
     * Document type to pre-filter templates
     */
    documentType?: "agreement" | "avenant" | "framework_contract";
}

const AgreementValidationModal = ({
    relatedResourceData,
    onClose,
    modalType,
    open,
    signatureProvider,
    documentType,
}: AgreementValidationModalProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { termAssociatedToTitle } = useAppSelector((state) => state.titles);
    const { workTerms } = useAppSelector((state) => state.works);
    const { getCompanySettings } = useCompanySettings();
    const [activeStep, setActiveStep] = useState(0);
    const [currentTemplateId, setCurrentTemplateId] = useState(-1);
    const [editorState, setEditorState] = useState({ stringifiedBlocks: "", html: "" });
    const [newContractId, setNewContractId] = useState(-1);
    const [docoonSignatories, setDocoonSignatories] = useState<DocoonSignatory[]>([]);
    const [docoonLoading, setDocoonLoading] = useState(false);
    const [docoonPreloadedEmail, setDocoonPreloadedEmail] = useState<string>();
    const [signatairesIds, setSignatairesIds] = useState("");
    const [docoonBlockingReason, setDocoonBlockingReason] = useState<string | null>(null);
    const [selectedSignatories, setSelectedSignatories] = useState<DocoonSignatoryWithRole[]>([]);
    const docoonHasInformed = selectedSignatories.some((s) => s.isInformed);
    const signSequentialDefault = !!getCompanySettings("docoon_sign_sequential_default");
    const [docoonOptions, setDocoonOptions] = useState<DocoonSendOptionsValue>(() =>
        getDefaultDocoonSendOptions(signSequentialDefault),
    );
    // Company settings may load after mount: re-apply the default on opening and once they arrive.
    useEffect(() => {
        if (open) setDocoonOptions(getDefaultDocoonSendOptions(signSequentialDefault));
    }, [open, signSequentialDefault]);
    const [pickedResource, setPickedResource] = useState<AgreementRelatedResourceData | null>(null);
    const [avenantTarget, setAvenantTarget] = useState<AvenantTarget | null>(null);
    const [signatureBehavior, setSignatureBehavior] = useState<SignatureBehavior | null>(
        modalType === "multiple" ? "multiple" : modalType === "single" ? "single" : null,
    );

    const hasResourcePickerStep = !relatedResourceData;
    const hasAvenantTargetStep = documentType === "avenant" && hasResourcePickerStep;
    const effectiveResourceData: AgreementRelatedResourceData = relatedResourceData ??
        pickedResource ?? {
            relatedResource: "term",
            relatedResourceId: -1,
            beneficiaryId: -1,
            titleId: -1,
            title: "",
            beneficiary: "",
        };
    const isResourceReady = !!relatedResourceData || !!pickedResource;

    const {
        beneficiaryId,
        titleId,
        workId,
        beneficiary,
        title,
        relatedResource,
        email: relatedResourceEmail,
    } = effectiveResourceData;

    const isFrameworkContract = documentType === "framework_contract";
    const { isDocoon } = useSignatureProvider(signatureProvider);
    const usesValidationChain = !!getCompanySettings("uses_agreement_validation_chain");
    const showDocoonOptionsStep = isDocoon && usesValidationChain;
    /**
     * Co-authors on the same work (book) or title (press), for the multi-signature step.
     * Needed here so we can decide whether to show the single/multi step.
     */
    const associatedTerms =
        relatedResource === "term"
            ? workId != null
                ? workTerms.filter((term) => term.workId === workId)
                : []
            : termAssociatedToTitle;
    const titleBeneficiaries = useMemo(() => {
        return getBeneficiariesOptions(associatedTerms);
    }, [associatedTerms]);
    // Multi-signature is for term contracts only: an article (press) has a single author, the other
    // beneficiaries of its title are not co-authors.
    const supportsMultiSignature = !isFrameworkContract && effectiveResourceData.relatedResource === "term";
    // When the picker step is present, the multi-signature toggle lives inline inside PickResourceStep,
    // so we don't need a dedicated behavior step here.
    const hasSignatureBehaviorStep =
        supportsMultiSignature &&
        documentType !== "avenant" &&
        !modalType &&
        !hasResourcePickerStep &&
        titleBeneficiaries.options.length > 1;
    // 0 hides the inline multi-signature toggle of PickResourceStep
    const otherBeneficiariesCount = supportsMultiSignature
        ? Math.max(titleBeneficiaries.options.length - (titleBeneficiaries.options.length > 0 ? 1 : 0), 0)
        : 0;
    const hasMultiSignature = supportsMultiSignature && signatureBehavior === "multiple";

    const multiStepSubtitle = isResourceReady ? (
        <SmallBody
            color="grey"
            style={{ maxWidth: "22rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
        >
            {t(
                documentType === "avenant"
                    ? "ContractSettings.TypeAvenant"
                    : documentType === "framework_contract"
                      ? "ContractValidation.FrameworkContract"
                      : "ContractValidationEdit.Contract",
            )}{" "}
            <span style={{ fontWeight: 600 }}>{title}</span> {t("ContractValidationEdit.For")}{" "}
            <span style={{ fontWeight: 600 }} title={beneficiary}>
                {beneficiary}
            </span>
        </SmallBody>
    ) : undefined;

    const defaultValues: TFormCreateAgreement = useMemo(() => {
        return {
            signatory: { firstname: "", id: undefined },
            beneficiaryIds: beneficiaryId > 0 ? [beneficiaryId] : [],
        };
    }, [effectiveResourceData]);

    useEffect(() => {
        reset();
    }, [defaultValues]);

    const methods = useForm({
        defaultValues,
        mode: "all",
    });

    const { handleSubmit, reset } = methods;

    const selectedBeneficiaryIds = methods.watch("beneficiaryIds");
    const docoonCoBeneficiaries: CoBeneficiarySigner[] = hasMultiSignature
        ? (selectedBeneficiaryIds ?? [])
              .filter((id) => id !== beneficiaryId)
              .map((id) => ({
                  beneficiaryId: id,
                  email: titleBeneficiaries.beneficiary[id]?.email ?? "",
                  name: titleBeneficiaries.beneficiary[id]?.name ?? "",
              }))
        : [];

    const onOpenContract = (id: number) => {
        onClose(true, id);
    };

    const searchDocoonSignatories = useCallback(
        async (query: string): Promise<DocoonSignatory[]> => {
            const result = await dispatch(fetchDocoonSignatories({ email: query }));
            if (fetchDocoonSignatories.fulfilled.match(result)) return result.payload;
            return [];
        },
        [dispatch],
    );

    useEffect(() => {
        if (!isResourceReady) return;
        if (hasMultiSignature || isDocoon) {
            if (relatedResource === "term" && workId != null) {
                dispatch(fetchWorkTerms(workId));
            } else if (titleId != null && titleId > 0 && !termAssociatedToTitle.length) {
                dispatch(fetchTermAssociatedToTitle({ titleId }));
            }
        }

        dispatch(fetchAgreementsTemplate()).then((res) => {
            const templates = res.payload.data;
            const effectiveType = documentType ?? "agreement";
            const typed = templates.filter((tmpl) => tmpl.type === effectiveType);
            const defaultAgreement = typed.find((template) => template.name === "Modèle par défaut");
            setCurrentTemplateId(defaultAgreement?.id ?? typed[0]?.id);
        });
        dispatch(fetchChannels());
    }, [isResourceReady]);

    const mainEmail = relatedResourceEmail ?? titleBeneficiaries.beneficiary[beneficiaryId]?.email ?? undefined;
    useEffect(() => {
        if (!isResourceReady || !isDocoon || !mainEmail) return;
        // Another resource was picked: the signatories restored on "Back" belong to the previous one
        setSelectedSignatories([]);
        setDocoonLoading(true);
        dispatch(fetchDocoonSignatories({ titleId: titleId || undefined, mainEmail }))
            .then((res) => {
                if (fetchDocoonSignatories.fulfilled.match(res)) {
                    setDocoonSignatories(res.payload);
                    setDocoonPreloadedEmail(mainEmail);
                }
            })
            .finally(() => setDocoonLoading(false));
    }, [isResourceReady, isDocoon, titleId, mainEmail]);

    const handleStep = {
        change: (_: any, idx: number) => setActiveStep(idx),
        next: () => setActiveStep((prev) => prev + 1),
        previous: () => setActiveStep((prev) => prev - 1),
    };

    const onSubmit: SubmitHandler<TFormCreateAgreement> = async (form) => {
        const { html, stringifiedBlocks: blocks } = editorState;
        const { relatedResourceId, relatedResource } = effectiveResourceData;

        const docoonPayload = isDocoon
            ? {
                  signatories: toDocoonSignatoriesPayload(selectedSignatories),
                  ...(showDocoonOptionsStep && toDocoonSendOptionsPayload(docoonOptions, docoonHasInformed)),
              }
            : undefined;

        const response = await dispatch(
            postAgreement({
                relatedResourceId,
                relatedResource,
                templateId: currentTemplateId,
                userVariables: {},
                html,
                blocks,
                publisherSignerId: form.signatory.id,
                ...(hasMultiSignature && { beneficiaryIds: form.beneficiaryIds?.reverse() }),
                ...(isDocoon && { signatureProvider: "docoon" }),
                // Framework contracts are stored as beneficiary_agreements.type='agreement'
                // (that column only accepts agreement|avenant). Only forward the wizard-level
                // type when it maps directly to the DB column.
                ...(documentType && documentType !== "framework_contract" && { type: documentType }),
                ...(docoonPayload && { docoon: docoonPayload }),
            }),
        );
        if (response.meta.requestStatus !== "fulfilled") return;

        const agreementId: number = response.payload.id;

        if (!isDocoon) setNewContractId(agreementId);
        onOpenContract(agreementId);
    };

    return (
        <FormProvider {...methods}>
            <Modal open={open} onClose={() => onClose()}>
                <form
                    className="agreement-validation-modal"
                    onSubmit={handleSubmit(onSubmit)}
                    onKeyDown={preventSubmit}
                >
                    <MultiStep
                        id="form"
                        activeStep={activeStep}
                        onStepChange={handleStep.change}
                        title={t(
                            documentType === "avenant"
                                ? "ContractSettings.AvenantCreation"
                                : documentType === "framework_contract"
                                  ? "ContractSettings.FrameworkContractCreation"
                                  : "ContractSettings.ContractCreation",
                        )}
                        subtitle={multiStepSubtitle}
                        onClose={() => onClose()}
                        animatedStepper
                        style={{
                            width: "min(1060px, calc(100vw - 32px))",
                            minWidth: "auto",
                            maxHeight: "calc(100vh - 32px)",
                        }}
                        steps={[
                            {
                                title: t("ContractSettings.AvenantTargetStepTitle"),
                                component: (
                                    <AvenantTargetStep
                                        value={avenantTarget}
                                        onChange={(next) => {
                                            setAvenantTarget(next);
                                            setPickedResource(null);
                                        }}
                                        onClose={() => onClose()}
                                        onContinue={() => handleStep.next()}
                                    />
                                ),
                                hidden: !hasAvenantTargetStep,
                            },
                            {
                                title: t("ContractSettings.PickResourceStepTitle"),
                                component: (
                                    <PickResourceStep
                                        documentType={documentType ?? "agreement"}
                                        fetchAsFrameworkContract={avenantTarget === "frameworkContract"}
                                        onClose={() => onClose()}
                                        onPrevious={hasAvenantTargetStep ? handleStep.previous : undefined}
                                        onPick={(picked) => {
                                            if (!picked) {
                                                setPickedResource(null);
                                                setSignatureBehavior(null);
                                                return;
                                            }
                                            setPickedResource({
                                                // beneficiary_agreements.relatedResource uses camelCase
                                                // for framework contracts ('frameworkContract').
                                                relatedResource:
                                                    picked.type === "framework_contract"
                                                        ? "frameworkContract"
                                                        : picked.type,
                                                relatedResourceId: picked.id,
                                                beneficiaryId: picked.beneficiaryId,
                                                titleId: picked.titleId,
                                                workId: picked.workId ?? undefined,
                                                title: picked.name,
                                                beneficiary: picked.beneficiaryName,
                                                email: picked.email ?? undefined,
                                            });
                                        }}
                                        onContinue={() => handleStep.next()}
                                        otherBeneficiariesCount={otherBeneficiariesCount}
                                        multiSignature={signatureBehavior === "multiple"}
                                        onToggleMultiSignature={(multi) =>
                                            setSignatureBehavior(multi ? "multiple" : "single")
                                        }
                                    />
                                ),
                                hidden: !hasResourcePickerStep,
                            },
                            {
                                title: t("ContractSettings.SignatureBehaviorStepTitle"),
                                component: (
                                    <SignatureBehaviorStep
                                        value={signatureBehavior}
                                        onChange={setSignatureBehavior}
                                        onClose={() => onClose()}
                                        onContinue={() => handleStep.next()}
                                    />
                                ),
                                hidden: !hasSignatureBehaviorStep,
                            },
                            {
                                title: t("ContractSettings.Beneficiaries"),
                                component: (
                                    <BeneficiariesStep
                                        handleNextStep={handleStep.next}
                                        titleBeneficiaries={titleBeneficiaries as TTitleBeneficiaries}
                                        onClose={() => onClose()}
                                        defaultBeneficiaryId={defaultValues.beneficiaryIds?.[0] as number}
                                        onPrevious={
                                            hasSignatureBehaviorStep || hasResourcePickerStep
                                                ? handleStep.previous
                                                : undefined
                                        }
                                    />
                                ),
                                hidden: !hasMultiSignature,
                            },
                            {
                                title: t("DocoonSignModal.StepSignatories"),
                                component: (
                                    <div className="m-8">
                                        <div className="flex flex-col gap-4">
                                            <DocoonSignatoryStep
                                                mainSignerEmail={
                                                    titleBeneficiaries.beneficiary[beneficiaryId]?.email ??
                                                    relatedResourceEmail ??
                                                    ""
                                                }
                                                mainSignerName={beneficiary}
                                                docoonSignatories={docoonSignatories}
                                                isLoading={docoonLoading}
                                                onSearch={searchDocoonSignatories}
                                                onChange={(ids) => setSignatairesIds(ids)}
                                                onBlockingReasonChange={setDocoonBlockingReason}
                                                onSignatoriesChange={(sigs) => setSelectedSignatories(sigs)}
                                                coBeneficiaries={docoonCoBeneficiaries}
                                                documentType={documentType}
                                                preloadedEmail={docoonPreloadedEmail}
                                                // The stepper unmounts this step: restore the picks on "Back"
                                                {...toInitialSignatoryBlocks(
                                                    selectedSignatories,
                                                    docoonCoBeneficiaries,
                                                )}
                                            />
                                            <div className="h-px bg-[#f3f4f6]" />
                                            <div className="flex justify-between">
                                                {/* First step when the resource is already known (e.g. an avenant): nothing to go back to */}
                                                {activeStep > 0 ? (
                                                    <StyledButton buttonType="secondary" onClick={handleStep.previous}>
                                                        {t("DocoonSignModal.Back")}
                                                    </StyledButton>
                                                ) : (
                                                    <StyledButton buttonType="secondary" onClick={() => onClose()}>
                                                        {t("DocoonSignModal.Cancel")}
                                                    </StyledButton>
                                                )}
                                                <StyledButton
                                                    buttonType="primary"
                                                    onClick={handleStep.next}
                                                    disabled={!signatairesIds}
                                                    tooltipText={docoonBlockingReason ?? ""}
                                                >
                                                    {t("DocoonSignModal.Next")}
                                                </StyledButton>
                                            </div>
                                        </div>
                                    </div>
                                ),
                                hidden: !isDocoon,
                            },
                            {
                                title: t("ContractSettings.Model"),
                                component: (
                                    <ChooseTemplateStep
                                        onClose={() => onClose()}
                                        handlePreviousStep={handleStep.previous}
                                        handleNextStep={handleStep.next}
                                        currentTemplateId={currentTemplateId}
                                        setCurrentTemplateId={setCurrentTemplateId}
                                        setEditorState={setEditorState}
                                        hasMultiSignature={hasMultiSignature}
                                        titleBeneficiaries={titleBeneficiaries}
                                        beneficiaryId={beneficiaryId}
                                        workId={workId}
                                        isDocoon={isDocoon}
                                        isLastStep={isDocoon && !showDocoonOptionsStep}
                                        documentType={documentType}
                                        hasPreviousStep={
                                            hasSignatureBehaviorStep || hasAvenantTargetStep || hasResourcePickerStep
                                        }
                                    />
                                ),
                            },
                            {
                                title: t("DocoonSignModal.StepOptions"),
                                component: (
                                    <div className="m-8 flex flex-col gap-4">
                                        <DocoonSendOptionsFields
                                            value={docoonOptions}
                                            onChange={setDocoonOptions}
                                            forceSequential={docoonHasInformed}
                                        />
                                        <div className="h-px bg-[#f3f4f6]" />
                                        <div className="flex justify-between">
                                            <StyledButton buttonType="secondary" onClick={handleStep.previous}>
                                                {t("DocoonSignModal.Back")}
                                            </StyledButton>
                                            <StyledButton
                                                buttonType="primary"
                                                onClick={handleSubmit(onSubmit)}
                                                disabled={!isDocoonSendOptionsComplete(docoonOptions)}
                                            >
                                                {t("DocoonSignModal.ConfirmGenerateDraft")}
                                            </StyledButton>
                                        </div>
                                    </div>
                                ),
                                hidden: !showDocoonOptionsStep,
                            },
                            {
                                title: t("ContractSettings.Signatory"),
                                component: <SignatoriesStep handlePreviousStep={handleStep.previous} />,
                                hidden: isDocoon,
                            },
                        ]}
                    />
                </form>
            </Modal>
        </FormProvider>
    );
};

export default AgreementValidationModal;
